// Baked deform-timeline contract (Order 16.0) — the seam between Marionette's
// Character Animator warp solver (Lane A, Order 15.7) and Arcade's Phaser mesh
// player (Lane B, Orders 16.1–16.3). See arcade_slice_plan_v0.
//
// The plan's locked decision: "Rich solver at author time, cheap playback at
// runtime." Marionette runs the full contour-mesh + sticks + Limb-IK warp
// solver on the desktop and BAKES the result to the flat, framework-neutral
// timeline defined here. Arcade plays it back through a plain deforming-mesh
// renderer (Phaser). The expensive solve never runs on the phone; both ends
// share one deforming-mesh geometry, so what the author sees is what ships.
//
// This module is the CONTRACT (types) plus a dependency-free reference player
// (DeformTimeline + scene-clock time → deformed vertex positions) and a fixture
// timeline, so Lane B can build and test against real data before 15.7's export
// exists. Renderer-agnostic, like the rest of anim-schema: NO Phaser, NO Pixi,
// NO WebGL, no DOM — pure data + math. Each host wires these positions into its
// own mesh primitive (Phaser.GameObjects.Mesh / Rope, a Pixi SimpleMesh, or a
// canvas triangle blit).
//
// Coordinate spaces:
//   * Mesh vertices and vertex offsets live in the PART's own local space
//     (the same rig-space the part's artwork is authored in). Placement of the
//     part in the scene/film frame is the host's job (Marionette's SceneTransform,
//     Phaser's game-object transform) — the deform math is placement-independent.
//   * Time is SECONDS on the shared scene clock — the same `t` that drives puppet
//     actions, Rhubarb visemes, and collage motion (scene.ts). `fps` is recorded
//     for time↔frame mapping but `time_seconds` on each key is authoritative.

import { CH_VISEMES, type Viseme } from './layer';
import { LOCKED_FPS } from './composition';

export const DEFORM_TIMELINE_SCHEMA = 'deform-timeline/v0' as const;

// A 2D point / vector in a part's local rig space. anim-schema has no general
// Vec2 (PartPivot is a pivot-specific {x,y}); this is the mesh's vertex type and
// the offset type, so positions and offsets add component-wise without casts.
export interface Vec2 {
  x: number;
  y: number;
}

// One deforming part's triangle mesh in REST pose (the neutral, un-warped
// shape). Vertices, UVs, and per-key offsets are all PARALLEL arrays indexed by
// vertex — offsets[i] displaces vertices[i], and uvs[i] is vertices[i]'s texture
// coordinate. The renderer uploads `triangles` as the index buffer once and only
// re-uploads positions per frame.
export interface DeformMesh {
  // Stable handle for the part this mesh deforms — Marionette's PartLayer.rig_key
  // (e.g. 'left-thigh'). The consumer binds artwork/atlas frames by this key, so
  // it survives renames and uuid churn. See keyframe.ts / layer.ts rig_key.
  part_key: string;
  // Optional Character Animator handle tag (Head, Left Knee, …) this part carries,
  // for hosts that attach IK targets, hit/hurt anchors, or physics pins to named
  // joints. Absent = an untagged deforming part. See ADOBE_BODY_TAGS.
  tag?: AdobeBodyTag;
  // Rest-pose vertex positions, part-local space. length === uvs.length and ===
  // every key's offsets.length.
  vertices: Vec2[];
  // Texture coordinates, one per vertex, normalized 0..1 into the part's source
  // image (or atlas frame). Parallel to `vertices`. Static — a warp moves
  // vertices, never their UVs (the texture flows with the mesh).
  uvs: Vec2[];
  // Flat triangle index list into `vertices`; length is a multiple of 3. Winding
  // is the author's (Marionette emits CCW); the player does not re-wind, so a
  // backface-culling host should match Marionette's convention.
  triangles: number[];
  // The artwork the UVs address, as a host-resolved key — a Phaser texture key,
  // a Marionette asset path, an atlas frame name. The bake knows which image a
  // part samples and the host cannot draw the mesh without it, so it belongs on
  // the seam rather than in a side-table the two ends have to agree on twice.
  // Optional only because a geometry-only timeline (tests, the fixture) carries
  // no artwork; any timeline a renderer draws must set it.
  texture?: string;
  // Natural pixel size of the source artwork the UVs address (informational —
  // lets a host scale local-space vertices into pixels without guessing).
  source_width?: number;
  source_height?: number;
}

// A baked keyframe: per-vertex offsets from the rest pose at one scene-clock
// time. A dense bake emits one key per rendered frame; a decimated bake emits
// sparse keys — the player linearly interpolates either way, so both round-trip
// through the same format. `offsets[i]` is added to `mesh.vertices[i]`.
export interface DeformVertexKey {
  time_seconds: number;   // on the shared scene clock; keys are sorted ascending
  offsets: Vec2[];        // length === mesh.vertices.length
}

// One part's full deform track: its rest mesh plus the sorted offset keys.
export interface DeformPartTrack {
  mesh: DeformMesh;
  // Sorted ascending by time_seconds; length >= 1. A single key is a static
  // (non-animating) deformation held for the whole loop.
  keys: DeformVertexKey[];
}

// Loop metadata for the whole timeline (one clip: 'idle' / 'walk' / 'jump' /
// 'attack'). Mirrors Marionette's loop model (set_loop / seal_loop).
export interface DeformLoop {
  name: string;               // clip name, e.g. 'walk'
  duration_seconds: number;   // clip length on the scene clock; > 0
  // Playback wraps: at t >= duration the player restarts at t mod duration.
  // false = one-shot (hold the last key past the end).
  loop: boolean;
  // The bake matched the last frame to the first (endpoints coincide) so a
  // wrapping loop has no visible seam — Marionette's seal_loop. Informational for
  // the player (wrapping already produces the seam-free result when true); a host
  // may warn if `loop` is true but `sealed` is false. Absent = not asserted.
  sealed?: boolean;
}

// The baked deform timeline: the complete artifact Marionette exports and Arcade
// consumes. One clip, N deforming parts, all on the shared scene clock.
export interface DeformTimeline {
  schema: typeof DEFORM_TIMELINE_SCHEMA;   // versioned contract tag
  loop: DeformLoop;
  // The frame rate the solver sampled at (defaults to the studio LOCKED_FPS).
  // Informational: `time_seconds` on each key is authoritative, but a host that
  // wants integer frame indices maps them through fps.
  fps: number;
  parts: DeformPartTrack[];
  // Convenience index of the Adobe handle tags present across `parts` (deduped),
  // so a host can bind IK / attachments without walking every mesh. Optional and
  // derivable via collectTags(); a producer may omit it.
  tags?: AdobeBodyTag[];
}

export const DEFORM_CLIP_SET_SCHEMA = 'deform-clip-set/v0' as const;

// One rigged actor's full baked animation set: every clip it can play, keyed by
// clip name. A DeformTimeline is ONE clip; a character needs several ('idle',
// 'walk', 'jump', 'attack', 'hit') and a runtime state machine switches between
// them, so this is the artifact Marionette exports per actor and a game loads as
// one file.
//
// Clip names are plain strings, not a fixed union: which loops an actor has is
// the CONTENT's business, not the schema's. A host that only accepts a known set
// (Arcade's LoopName) narrows on its own side.
export interface DeformClipSet {
  schema: typeof DEFORM_CLIP_SET_SCHEMA;
  // Stable handle for the actor/puppet these clips animate (Marionette's rig id).
  rig_key: string;
  // Clip name → its baked timeline. Every clip must cover the SAME parts with the
  // same vertex counts (see validateDeformClipSet) — that invariant is what lets
  // a player upload the rest mesh once and merely swap which clip drives it.
  clips: Record<string, DeformTimeline>;
  // Which clip to play on load. Must be a key of `clips`. Absent = the host picks
  // (Arcade starts on 'idle').
  default_clip?: string;
}

// Structural checks for a clip set: every clip is itself valid, `default_clip`
// resolves, and the rest meshes agree across clips. The cross-clip agreement is
// the load-bearing one — a player that uploads geometry once and then switches
// clips will silently draw garbage if clip B's part list or vertex counts differ
// from clip A's. Returns human-readable problems; empty === valid.
export function validateDeformClipSet(set: DeformClipSet): string[] {
  const problems: string[] = [];
  if (set.schema !== DEFORM_CLIP_SET_SCHEMA) problems.push(`schema is '${set.schema}', expected '${DEFORM_CLIP_SET_SCHEMA}'`);

  const names = Object.keys(set.clips);
  if (names.length === 0) problems.push('clip set has no clips');
  if (set.default_clip !== undefined && !(set.default_clip in set.clips)) {
    problems.push(`default_clip '${set.default_clip}' is not a key of clips`);
  }

  for (const name of names) {
    for (const p of validateDeformTimeline(set.clips[name])) problems.push(`clip '${name}': ${p}`);
  }

  // Compare every clip's part signature (ordered part_key + vertex count) to the
  // first clip's. Order matters: hosts index parts positionally.
  const [first, ...rest] = names;
  if (first !== undefined) {
    const sig = (n: string) => set.clips[n].parts.map((t) => `${t.mesh.part_key}:${t.mesh.vertices.length}`).join(',');
    const want = sig(first);
    for (const name of rest) {
      const got = sig(name);
      if (got !== want) problems.push(`clip '${name}' part signature [${got}] does not match clip '${first}' [${want}]`);
    }
  }
  return problems;
}

// ---------------------------------------------------------------------------
// The Adobe tag vocabulary
// ---------------------------------------------------------------------------
//
// arcade_slice_plan_v0: "Adobe's tag vocabulary wholesale (Left/Right Shoulder,
// Elbow, Wrist, Waist, Hip, Knee, Heel, Toe, Head, and the viseme set) so
// existing rigs import with rigging intact." These are Character Animator's
// handle TAGS (the Tags panel) — what the Walk behavior and Limb IK bind to —
// distinct from the group-NAME conventions in puppet.ts (CH_CONVENTION_NAMES),
// which classify rig structure at import. Tags name JOINTS for motion; the deform
// mesh carries them so a runtime can anchor IK targets and hit/hurt boxes to
// named limbs.

// The centreline (unsided) body handle tags.
export const ADOBE_CENTER_TAGS = ['Head', 'Neck', 'Waist', 'Root'] as const;

// The sided limb-joint tags — emitted per side as 'Left X' / 'Right X'. This is
// the wholesale CH Walk/IK joint chain: shoulder→elbow→wrist and hip→knee→heel→toe.
export const ADOBE_LIMB_TAGS = [
  'Shoulder', 'Elbow', 'Wrist',
  'Hip', 'Knee', 'Heel', 'Toe',
] as const;

export type AdobeCenterTag = typeof ADOBE_CENTER_TAGS[number];
export type AdobeLimbTag = typeof ADOBE_LIMB_TAGS[number];
export type AdobeSide = 'Left' | 'Right';
// A sided limb tag string, e.g. 'Left Knee'. Kept as a template-literal type so
// it stays a plain string at the JSON boundary (no enum), matching how the rest
// of anim-schema types string ids.
export type AdobeSidedTag = `${AdobeSide} ${AdobeLimbTag}`;

// The full Adobe BODY handle-tag set: centre tags + every sided limb tag.
export type AdobeBodyTag = AdobeCenterTag | AdobeSidedTag;

// The concrete list of every valid body tag (centre + Left/Right × each limb),
// for validation and UI. Built once so it can't drift from the type above.
export const ADOBE_BODY_TAGS: readonly AdobeBodyTag[] = (() => {
  const sided: AdobeSidedTag[] = [];
  for (const side of ['Left', 'Right'] as const) {
    for (const limb of ADOBE_LIMB_TAGS) sided.push(`${side} ${limb}` as AdobeSidedTag);
  }
  return [...ADOBE_CENTER_TAGS, ...sided];
})();

const ADOBE_BODY_TAG_SET = new Set<string>(ADOBE_BODY_TAGS);

// The full Adobe tag vocabulary = body handle tags ∪ the mouth viseme set. The
// visemes ride the discrete mouth-swap channel (layer.ts CH_VISEMES), not the
// deform mesh, but they belong to the same Adobe vocabulary — this union names
// the whole contract in one place so a consumer can reason over every tag a rig
// might carry.
export type AdobeTag = AdobeBodyTag | Viseme;
export const ADOBE_TAGS: readonly AdobeTag[] = [...ADOBE_BODY_TAGS, ...CH_VISEMES];

// Canonicalize a raw handle name to a valid AdobeBodyTag, tolerating case, '_'/
// '-' separators, extra whitespace, and the 'L '/'R ' side abbreviations CH
// files use ('l knee' → 'Left Knee'). Returns null for a non-body-tag name. Kept
// deliberately small — the same tolerant spirit as puppet.ts canonName, but this
// module stays self-contained (no import from the importer).
export function normalizeAdobeTag(raw: string): AdobeBodyTag | null {
  let n = (raw ?? '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  if (!n) return null;
  n = n.replace(/^l\s+/, 'left ').replace(/^r\s+/, 'right ');
  const titled = n.replace(/\b\w/g, (c) => c.toUpperCase()); // Title Case each word
  return ADOBE_BODY_TAG_SET.has(titled) ? (titled as AdobeBodyTag) : null;
}

// Collect the deduped Adobe body tags actually present across a timeline's parts,
// in ADOBE_BODY_TAGS order (stable). Producers use this to fill DeformTimeline.tags.
export function collectTags(tl: DeformTimeline): AdobeBodyTag[] {
  const present = new Set<AdobeBodyTag>();
  for (const p of tl.parts) if (p.mesh.tag) present.add(p.mesh.tag);
  return ADOBE_BODY_TAGS.filter((t) => present.has(t));
}

// ---------------------------------------------------------------------------
// Reference mesh player — pure, framework-neutral
// ---------------------------------------------------------------------------
//
// (DeformTimeline, scene-clock time) → deformed vertex positions. This is the
// "plain mesh renderer" playback path both ends share. A host calls
// playDeformTimeline(tl, t) each frame and feeds the returned positions +
// triangles + uvs into its own mesh primitive.

// One part's deformed geometry at a sampled time — everything a mesh draw needs.
export interface DeformedPart {
  part_key: string;
  tag?: AdobeBodyTag;
  positions: Vec2[];   // rest vertices + interpolated offsets, part-local space
  uvs: Vec2[];         // shared reference to the mesh's static UVs (not copied)
  triangles: number[]; // shared reference to the mesh's static index list
}

// A whole timeline sampled at one instant.
export interface DeformedFrame {
  time_seconds: number;      // the resolved (loop-wrapped) time actually sampled
  parts: DeformedPart[];
}

// Resolve a raw scene-clock time to a time within [0, duration] under the loop's
// policy: wrap when `loop`, else clamp. Negative times clamp/wrap to the start.
export function resolveLoopTime(t: number, loop: DeformLoop): number {
  const d = loop.duration_seconds;
  if (!(d > 0)) return 0;
  if (!loop.loop) return t <= 0 ? 0 : t >= d ? d : t;
  // Wrap into [0, d) — JS '%' keeps the sign, so normalize negatives.
  const m = t % d;
  return m < 0 ? m + d : m;
}

// Linear-interpolate one part's per-vertex offsets at time `t` (already
// loop-resolved), then add them to the rest vertices → deformed positions.
// Holds the first key before its time and the last key after (baked timelines
// are self-contained; there is no extrapolation). O(log n) key lookup so a dense
// per-frame bake stays cheap.
export function deformPartAt(track: DeformPartTrack, t: number): Vec2[] {
  const { mesh, keys } = track;
  const verts = mesh.vertices;
  const n = verts.length;

  if (keys.length === 0) return verts.map((v) => ({ x: v.x, y: v.y }));

  // Binary search for the last key with time <= t.
  let lo = 0, hi = keys.length - 1, idx = 0;
  if (t <= keys[0].time_seconds) idx = 0;
  else if (t >= keys[hi].time_seconds) idx = hi;
  else {
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (keys[mid].time_seconds <= t) { idx = mid; lo = mid + 1; }
      else hi = mid - 1;
    }
  }

  const a = keys[idx];
  const b = keys[idx + 1];
  // At/after the last key, or exactly on a key with no successor → hold `a`.
  if (!b || t <= a.time_seconds) {
    const out: Vec2[] = new Array(n);
    for (let i = 0; i < n; i++) out[i] = { x: verts[i].x + a.offsets[i].x, y: verts[i].y + a.offsets[i].y };
    return out;
  }

  const span = b.time_seconds - a.time_seconds;
  const f = span > 0 ? (t - a.time_seconds) / span : 0; // guard coincident keys
  const out: Vec2[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const oa = a.offsets[i], ob = b.offsets[i];
    out[i] = {
      x: verts[i].x + oa.x + (ob.x - oa.x) * f,
      y: verts[i].y + oa.y + (ob.y - oa.y) * f,
    };
  }
  return out;
}

// Sample the whole timeline at scene-clock time `t`. Returns per-part deformed
// positions plus the static uvs/triangles (shared references — do not mutate).
export function playDeformTimeline(tl: DeformTimeline, t: number): DeformedFrame {
  const rt = resolveLoopTime(t, tl.loop);
  return {
    time_seconds: rt,
    parts: tl.parts.map((track) => ({
      part_key: track.mesh.part_key,
      tag: track.mesh.tag,
      positions: deformPartAt(track, rt),
      uvs: track.mesh.uvs,
      triangles: track.mesh.triangles,
    })),
  };
}

// Flatten Vec2[] to an interleaved [x0,y0,x1,y1,…] number[] — the shape most
// GPU mesh primitives (Phaser.Mesh, Pixi) want for a position buffer. Kept here
// so the two hosts don't each reinvent the packing.
export function flattenVec2(v: Vec2[]): number[] {
  const out = new Array<number>(v.length * 2);
  for (let i = 0; i < v.length; i++) { out[i * 2] = v[i].x; out[i * 2 + 1] = v[i].y; }
  return out;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

// Structural checks a producer/consumer can run before trusting a timeline.
// Returns a list of human-readable problems; empty === valid. Catches the
// parallel-array invariants the player relies on (offset/uv/vertex length match,
// triangle index bounds, sorted keys, positive duration) so a malformed bake
// fails loudly at the seam instead of drawing garbage.
export function validateDeformTimeline(tl: DeformTimeline): string[] {
  const problems: string[] = [];
  if (tl.schema !== DEFORM_TIMELINE_SCHEMA) problems.push(`schema is '${tl.schema}', expected '${DEFORM_TIMELINE_SCHEMA}'`);
  if (!(tl.loop.duration_seconds > 0)) problems.push(`loop.duration_seconds must be > 0 (got ${tl.loop.duration_seconds})`);
  if (tl.loop.loop && tl.loop.sealed === false) problems.push(`loop.loop is true but sealed is false — wrapping may show a seam`);
  if (tl.parts.length === 0) problems.push('timeline has no parts');

  for (const track of tl.parts) {
    const { mesh, keys } = track;
    const label = `part '${mesh.part_key}'`;
    const n = mesh.vertices.length;
    if (n === 0) problems.push(`${label}: mesh has no vertices`);
    if (mesh.uvs.length !== n) problems.push(`${label}: uvs length ${mesh.uvs.length} !== vertices length ${n}`);
    if (mesh.triangles.length % 3 !== 0) problems.push(`${label}: triangles length ${mesh.triangles.length} is not a multiple of 3`);
    for (const ix of mesh.triangles) {
      if (!Number.isInteger(ix) || ix < 0 || ix >= n) { problems.push(`${label}: triangle index ${ix} out of range [0, ${n})`); break; }
    }
    if (mesh.tag && !ADOBE_BODY_TAG_SET.has(mesh.tag)) problems.push(`${label}: tag '${mesh.tag}' is not an Adobe body tag`);
    if (keys.length === 0) problems.push(`${label}: track has no keys`);
    for (let k = 0; k < keys.length; k++) {
      if (keys[k].offsets.length !== n) problems.push(`${label}: key ${k} offsets length ${keys[k].offsets.length} !== vertices length ${n}`);
      if (k > 0 && keys[k].time_seconds < keys[k - 1].time_seconds) problems.push(`${label}: keys not sorted ascending at index ${k}`);
    }
  }
  return problems;
}

// ---------------------------------------------------------------------------
// Fixture — a real bent-limb walk clip for Lane B tests
// ---------------------------------------------------------------------------
//
// A minimal two-part rig (thigh + shin, a Left leg) that swings and bends across
// a half-second sealed walk loop, so Lane B can render, wrap, and interpolate a
// genuine deform timeline before Marionette's 15.7 export lands. Each part is a
// 4-vertex quad (2 triangles) in a local space; offsets bend the lower edge to
// fake a knee. Endpoints are sealed (first key === last key) so the wrapping
// loop is seam-free.

// Build a unit quad's rest data (top-left origin, CCW triangles) at a given size.
function quad(w: number, h: number): { vertices: Vec2[]; uvs: Vec2[]; triangles: number[] } {
  return {
    vertices: [
      { x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h },
    ],
    uvs: [
      { x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 },
    ],
    triangles: [0, 1, 2, 0, 2, 3],
  };
}

const zero4: Vec2[] = [{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }];

export const FIXTURE_DEFORM_TIMELINE: DeformTimeline = {
  schema: DEFORM_TIMELINE_SCHEMA,
  fps: LOCKED_FPS,
  loop: { name: 'walk', duration_seconds: 0.5, loop: true, sealed: true },
  tags: ['Left Hip', 'Left Knee'],
  parts: [
    {
      // Thigh: swings its lower edge (the knee end) forward and back. Tagged at
      // the hip — the joint it pivots about.
      mesh: { part_key: 'left-thigh', tag: 'Left Hip', source_width: 100, source_height: 200, ...quad(100, 200) },
      keys: [
        { time_seconds: 0.0,  offsets: [{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: 14, y: 0 }, { x: 14, y: 0 }] },
        { time_seconds: 0.25, offsets: [{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: -14, y: 0 }, { x: -14, y: 0 }] },
        { time_seconds: 0.5,  offsets: [{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: 14, y: 0 }, { x: 14, y: 0 }] }, // sealed: === t0
      ],
    },
    {
      // Shin: bends at the knee — its lower (heel) edge lags and lifts mid-stride.
      mesh: { part_key: 'left-shin', tag: 'Left Knee', source_width: 90, source_height: 180, ...quad(90, 180) },
      keys: [
        { time_seconds: 0.0,  offsets: zero4 },
        { time_seconds: 0.25, offsets: [{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: -22, y: -30 }, { x: -22, y: -30 }] },
        { time_seconds: 0.5,  offsets: zero4 }, // sealed: === t0
      ],
    },
  ],
};

// A one-shot 'idle': the same two parts held at rest for a beat. Single key per
// part — the player's hold path. Shares FIXTURE_DEFORM_TIMELINE's part signature
// (same keys, same vertex counts) so the pair is a VALID clip set, which is the
// invariant Lane B's clip-switching test needs to exercise.
const FIXTURE_IDLE: DeformTimeline = {
  schema: DEFORM_TIMELINE_SCHEMA,
  fps: LOCKED_FPS,
  loop: { name: 'idle', duration_seconds: 1.0, loop: true, sealed: true },
  tags: ['Left Hip', 'Left Knee'],
  parts: FIXTURE_DEFORM_TIMELINE.parts.map((track) => ({
    mesh: track.mesh,
    keys: [{ time_seconds: 0, offsets: zero4 }],
  })),
};

// A two-clip actor: the fixture walk plus a rest idle. Lane B builds and tests
// clip switching against this before Marionette's 15.7 export lands.
export const FIXTURE_DEFORM_CLIP_SET: DeformClipSet = {
  schema: DEFORM_CLIP_SET_SCHEMA,
  rig_key: 'fixture-left-leg',
  default_clip: 'idle',
  clips: { idle: FIXTURE_IDLE, walk: FIXTURE_DEFORM_TIMELINE },
};
